import { computed, nextTick, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { groupBy, intersection, sortBy, uniq } from 'lodash';

import {
  ActivityIngredient,
  isHotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type {
  HotelIngredient,
  RecipeIngredient,
} from '@apps/main/modules/recipe/ingredients';

import {
  assertValue,
  ensure,
  exhaustiveGuard,
  getDifferenceInDays,
  getHotelKey,
  getLongDatePeriod,
  getShortDatePeriod,
  isDefined,
  readonlyRef,
  replaceOrDeleteValues,
  ToggleMode,
} from '@libs/utils';
import type {
  Polygon,
  OverviewHeartedStaticInfo,
  PortStaticInfo,
  TransportLeg,
  IngredientId,
  ActivityTicket,
  DestinationStaticInfo,
  ChronologicalItinerary,
  CarrierName,
  DecimalRangeType,
  OverviewDestinationStaticInfo,
  HeartedHotel,
  DetailsStaticInfo,
  DestinationInformation,
  TransportStaticInfo,
  FlightIngredientResponse,
  FerryIngredientResponse,
  CarrierStaticInfo,
  AvailableTransportIngredientResponseWithStaticInfo,
  DestinationProduct,
  ProductCheapestAvailabilityResponse,
  BoardBasisCode,
  ItineraryWithStaticInfo,
  HotelIngredientResponseWithStaticInfo,
  OwnStayIngredientResponseWithStaticInfo,
  ItineraryMainIngredientResponse,
  FlightIngredientResponseWithStaticInfo,
  FerryIngredientResponseWithStaticInfo,
  TransportIngredientResponseWithStaticInfo,
  HotelIngredientResponse,
  OwnStayIngredientResponse,
  ActivityIngredientResponseWithStaticInfo,
  OverviewActivityStaticInfo,
  HotelAlternativeOptionWithBasicRooms,
  TransportVehicle,
  OverviewStaticInfoResult,
  HotelBookableOptionWithRooms,
  ItineraryMainIngredientResponseWithStaticInfo,
  OwnTransportIngredientResponseWithStaticInfo,
  OwnTransportIngredientResponse,
} from '@libs/trip-planner-interfaces';
import {
  SearchTermType,
  PolygonType,
  IngredientType,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import {
  isAvailableActivityIngredientResponse,
  isAvailableHotelIngredientResponse,
  isAvailableTransportIngredientResponse,
  isAvailableTransportIngredientResponseWithStaticInfo,
  isFlightIngredientResponse,
  isHotelIngredientResponse,
  isHotelIngredientResponseWithStaticInfo,
  isOwnStayIngredientResponse,
  isOwnStayIngredientResponseWithStaticInfo,
  isOwnTransportIngredientResponse,
  isStayIngredientResponseWithStaticInfo,
  isTransportIngredientResponse,
  LOCAL_SESSION_ID,
} from '@libs/search-service-smart-search';
import { getIsSmallDevice } from '@libs/ui';
import { trackMonthChanged } from '@libs/google-analytics-events';

import { useRecipeStore } from '../recipe/recipeStore';
import { useEditingIngredientStore } from '../editingIngredient/editingIngredientStore';
import { useItineraryStore } from '../itinerary/itineraryStore';
import { usePreferredTimeStore } from '../preferredTimeStore';

interface DatePeriodForStopParams {
  format: 'long' | 'short';
  stop: ChronologicalItinerary;
  isLastStop?: boolean;
  isSmallDevice?: boolean;
}

export const useTripStore = defineStore('TripStore', () => {
  const itineraryStore = useItineraryStore();
  const recipeStore = useRecipeStore();
  const editingIngredientStore = useEditingIngredientStore();
  const preferredTimeStore = usePreferredTimeStore();
  const { startSearchDate, endSearchDate, preferredDay, expandedMonth } =
    storeToRefs(preferredTimeStore);
  const { clearPreferredDay, expandMonth, isDateInExpandedMonth } =
    preferredTimeStore;

  // State
  const isLocationManuallyChanged = ref<boolean>(false);
  const isSearchingFerriesStaticInfo = ref<boolean>(false);
  const isSearchingFlightsStaticInfo = ref<boolean>(false);
  const areAllSelectedRatesValid = ref<boolean>(true);
  const startingLocationIsManuallyTyped = ref(false);
  const endLocationIsManuallyTyped = ref<boolean>(false);
  const destinationAvailabilitySearchCounter = ref<number>(0);
  const staticDestinationAvailabilitySearchCounter = ref<number>(0);
  const staysShown = ref<number>(0);
  const transportListSearchCounter = ref<number>(0);
  const sessionId = ref<string>(LOCAL_SESSION_ID);
  const destinationWithAvailableActivities = ref<Record<string, boolean>>({});
  const months = ref<Date[]>(SemboDate.getNextMonthsArray());
  const carrierNames = ref<Record<string, CarrierName>>({});
  const ferryVehicleTypes = ref<TransportVehicle[]>();
  const airportStaticInfo = ref<Record<string, PortStaticInfo>>({});
  const seaportStaticInfo = ref<Record<string, PortStaticInfo>>({});
  const carrierStaticInfo = ref<Record<string, CarrierStaticInfo>>({});
  const newDestinationStaticInfo = ref<Record<string, DestinationStaticInfo>>(
    {}
  );
  const newDestinationAvailability = ref<
    Record<string, ProductCheapestAvailabilityResponse>
  >({});
  const destinationStaticInfo = ref<Record<string, DestinationStaticInfo>>({});
  const destinationAvailability = ref<
    Record<string, ProductCheapestAvailabilityResponse>
  >({});
  const heartedHotelsOverviewStaticInfo = ref<
    Record<string, OverviewHeartedStaticInfo>
  >({});
  const subIngredientsOverviewStaticInfo = ref<
    Record<string, OverviewActivityStaticInfo>
  >({});
  const destinationOverviewStaticInfo = ref<
    Record<string, OverviewDestinationStaticInfo>
  >({});
  const destinationsInformation = ref<
    Map<IngredientId, DestinationInformation>
  >(new Map());
  const transportList = ref<
    AvailableTransportIngredientResponseWithStaticInfo[]
  >([]);

  // TODO: move to itineraryStore when staticInfoStore is ready
  const hotelsInItinerary = computed<OverviewDestinationStaticInfo[]>(() => {
    if (!itineraryStore.selectedItinerary) {
      return [];
    }

    return itineraryStore.selectedItinerary.hotelOnlyItinerary
      .map((ingredientResponse) =>
        isAvailableHotelIngredientResponse(ingredientResponse)
          ? getOverviewStaticInfo(
              ingredientResponse.suggested.code,
              ingredientResponse.ingredientId
            )
          : undefined
      )
      .filter(isDefined);
  });

  const preselectedHotel = computed(() => {
    const hotel = hotelsInItinerary.value.find(
      (hotel) =>
        hotel.ingredientId ===
        editingIngredientStore.editingStayIngredient?.ingredientId
    );
    return hotel
      ? {
          ...hotel,
          isHearted: hotelIsHearted(hotel.hotelCode, hotel.ingredientId),
        }
      : undefined;
  });

  const chronologicalItinerary = computed<ChronologicalItinerary[]>(() => {
    if (!selectedItineraryWithStaticInfo.value) {
      return [];
    }
    const tripStartDate = selectedItineraryWithStaticInfo.value.date;

    return Object.entries(
      groupBy(selectedItineraryWithStaticInfo.value.itinerary, (ingredient) =>
        getBaseKeyForIngredient(ingredient)
      )
    )
      .sort(sortEntriesByDate)
      .map(([startDateStr, ingredients]) => {
        const cleanedDateStr = startDateStr.replace(/-\d+$/, '');
        const startDate = new Date(cleanedDateStr);

        const sortedIngredients = sortIngredientsByTransport(ingredients);

        const { dayPeriod, datePeriod, shortDatePeriod } = getDayAndDatePeriods(
          sortedIngredients,
          startDate,
          tripStartDate
        );

        const stayIngredientInGroup = sortedIngredients.find(
          isStayIngredientResponseWithStaticInfo
        );
        const numberOfNights = stayIngredientInGroup?.numberOfNights ?? 0;

        return {
          startDate,
          numberOfNights,
          ingredients: sortedIngredients,
          datePeriod,
          dayPeriod,
          shortDatePeriod,
        };
      });
  });

  const destinationProducts = computed<DestinationProduct[]>(() => {
    const editingHotel = editingIngredientStore.editingStayIngredient;
    if (!destinationStaticInfo.value || !editingHotel) {
      return [];
    }
    return Object.values(destinationStaticInfo.value)
      .filter((hotel) => hotel.ingredientId === editingHotel.ingredientId)
      .map<DestinationProduct>((hotel) => ({
        ...hotel,
        isHearted: hotelIsHearted(hotel.hotelCode, hotel.ingredientId),
        totalPrice:
          destinationAvailability.value[hotel.hotelCode]?.prices.totalPrice,
        grossPrice:
          destinationAvailability.value[hotel.hotelCode]?.prices.grossPrice,
        boardBasis: getRoomsBoardBasis(hotel.hotelCode),
        boardBasisOptions: getBoardBasisOptions(hotel.hotelCode),
      }));
  });

  const isSearchingDestinationAvailability = computed<boolean>(
    () =>
      destinationAvailabilitySearchCounter.value > 0 ||
      staticDestinationAvailabilitySearchCounter.value > 0
  );

  // TODO: move to itineraryStore when staticInfoStore is ready
  const selectedItineraryWithStaticInfo = computed<
    ItineraryWithStaticInfo | undefined
  >(() => {
    if (!itineraryStore.selectedItinerary) {
      return;
    }

    const itineraryWithStaticInfoOrUndefined =
      itineraryStore.selectedItinerary.itinerary.map(
        getIngredientResponseWithStaticInfo
      );

    if (
      itineraryWithStaticInfoOrUndefined.some(
        (ingredientResponseWithStaticInfo) => !ingredientResponseWithStaticInfo
      )
    ) {
      return;
    }

    const itineraryWithStaticInfo = itineraryWithStaticInfoOrUndefined as (
      | OwnStayIngredientResponseWithStaticInfo
      | HotelIngredientResponseWithStaticInfo
      | FlightIngredientResponseWithStaticInfo
      | FerryIngredientResponseWithStaticInfo
    )[];

    const ownStayItineraryWithStaticInfo = itineraryWithStaticInfo.filter(
      isOwnStayIngredientResponseWithStaticInfo
    );

    const hotelItineraryWithStaticInfo = itineraryWithStaticInfo.filter(
      isHotelIngredientResponseWithStaticInfo
    );

    return {
      ...itineraryStore.selectedItinerary,
      itinerary: itineraryWithStaticInfo,
      ownStayOnlyItinerary: ownStayItineraryWithStaticInfo,
      hotelOnlyItinerary: hotelItineraryWithStaticInfo,
    };
  });

  // Actions
  function getBaseKeyForIngredient(
    ingredient: ItineraryMainIngredientResponseWithStaticInfo
  ): string {
    const baseKey = new SemboDate(ingredient.date)
      .startOf('day')
      .get()
      .toISOString();

    return baseKey;
  }

  function getRoomsBoardBasis(code: string): BoardBasisCode[] {
    return (
      destinationAvailability.value[code]?.rooms
        .map((room) => room.boardBasis)
        .filter(isDefined) ?? []
    );
  }

  function getBoardBasisOptions(code: string): BoardBasisCode[] {
    return (
      destinationAvailability.value[code]?.boardBasisOptions.filter(
        isDefined
      ) ?? []
    );
  }

  function getDatePeriodForStop(params: DatePeriodForStopParams): string {
    let stopDuration = params.stop.numberOfNights;
    params.stop.ingredients.forEach((ingredient, index, originalArray) => {
      if (
        isAvailableTransportIngredientResponseWithStaticInfo(ingredient) &&
        index === originalArray.length - 1
      ) {
        const transportDuration = getTransportDuration(ingredient);
        stopDuration += transportDuration;
      }
    });
    return params.format === 'long'
      ? getLongDatePeriod(
          params.stop.startDate,
          new SemboDate(params.stop.startDate)
            .add({ days: stopDuration })
            .get(),
          params.isLastStop
        )
      : getShortDatePeriod(
          params.stop.startDate,
          new SemboDate(params.stop.startDate)
            .add({ days: stopDuration })
            .get(),
          params.isLastStop,
          params.isSmallDevice
        );
  }

  function getTransportDuration(
    transportIngredient: AvailableTransportIngredientResponseWithStaticInfo
  ): number {
    const transportDuration = getDifferenceInDays(
      transportIngredient.arrivalDate,
      transportIngredient.departureDate
    );
    return transportDuration;
  }

  function getFerryAllCarriers(ferryIngredientId: IngredientId): {
    allCarriers: CarrierName[];
    areAllCarriersAvailable: boolean;
  } {
    const { allCarrierCodes, areAllCarrierCodesAvailable } =
      getFerryAllCarrierCodes(ferryIngredientId);

    const allCarrierNames = allCarrierCodes.map<CarrierName | undefined>(
      (carrierCode) => carrierNames.value[carrierCode]
    );

    const areAllCarrierNamesAvailable = allCarrierNames.every(isDefined);

    return {
      allCarriers: allCarrierNames.filter(isDefined),
      areAllCarriersAvailable:
        areAllCarrierCodesAvailable && areAllCarrierNamesAvailable,
    };
  }

  function getFerryAllCarrierCodes(ferryIngredientId: IngredientId): {
    allCarrierCodes: string[];
    areAllCarrierCodesAvailable: boolean;
  } {
    const ferryIngredientCarrierFilters: string[] = [];

    try {
      const ferryIngredient = recipeStore.getFerryIngredient(ferryIngredientId);

      ferryIngredientCarrierFilters.push(
        ...ferryIngredient.filters.carrierFilters
      );
    } catch {
      return {
        allCarrierCodes: [],
        areAllCarrierCodesAvailable: true,
      };
    }

    if (!itineraryStore.selectedItinerary) {
      return {
        allCarrierCodes: ferryIngredientCarrierFilters,
        areAllCarrierCodesAvailable: !itineraryStore.isSearchingItineraries,
      };
    }

    let ferryIngredientResponses: FerryIngredientResponse[];
    try {
      ferryIngredientResponses = preferredDay.value
        ? [
            ensure(
              itineraryStore.selectedItinerary.ferryOnlyItinerary.find(
                (ferryIngredient) =>
                  ferryIngredient.ingredientId === ferryIngredientId
              )
            ),
          ]
        : itineraryStore.itineraries.map((itinerary) =>
            ensure(
              itinerary.ferryOnlyItinerary.find(
                (flightIngredient) =>
                  flightIngredient.ingredientId === ferryIngredientId
              )
            )
          );
    } catch {
      ferryIngredientResponses = [];
    }

    const responseCarrierCodes = ferryIngredientResponses.reduce(
      (carrierCodes: string[], response) => {
        if (isAvailableTransportIngredientResponse(response)) {
          carrierCodes.push(...response.allCarriers);
        }

        return carrierCodes;
      },
      []
    );

    const result = {
      allCarrierCodes: uniq([
        ...ferryIngredientCarrierFilters,
        ...responseCarrierCodes,
      ]),
      areAllCarrierCodesAvailable: true,
    };
    return result;
  }

  function getFlightAllCarriers(flightIngredientId: IngredientId): {
    allCarriers: CarrierName[];
    areAllCarriersAvailable: boolean;
  } {
    const { allCarrierCodes, areAllCarrierCodesAvailable } =
      getFlightAllCarrierCodes(flightIngredientId);

    const allCarrierNames = allCarrierCodes.map<CarrierName | undefined>(
      (carrierCode) => carrierNames.value[carrierCode]
    );

    const areAllCarrierNamesAvailable = allCarrierNames.every(isDefined);

    return {
      allCarriers: allCarrierNames.filter(isDefined),
      areAllCarriersAvailable:
        areAllCarrierCodesAvailable && areAllCarrierNamesAvailable,
    };
  }

  function getFlightAllCarrierCodes(flightIngredientId: IngredientId): {
    allCarrierCodes: string[];
    areAllCarrierCodesAvailable: boolean;
  } {
    const flightIngredientCarrierFilters: string[] = [];

    try {
      const flightIngredient =
        recipeStore.getFlightIngredient(flightIngredientId);
      flightIngredientCarrierFilters.push(
        ...flightIngredient.filters.carrierFilters
      );
    } catch {
      return {
        allCarrierCodes: [],
        areAllCarrierCodesAvailable: true,
      };
    }

    if (!itineraryStore.selectedItinerary) {
      return {
        allCarrierCodes: flightIngredientCarrierFilters,
        areAllCarrierCodesAvailable: !itineraryStore.isSearchingItineraries,
      };
    }

    let flightIngredientResponses: FlightIngredientResponse[];
    try {
      flightIngredientResponses = preferredDay.value
        ? [
            ensure(
              itineraryStore.selectedItinerary.flightOnlyItinerary.find(
                (flightIngredient) =>
                  flightIngredient.ingredientId === flightIngredientId
              )
            ),
          ]
        : itineraryStore.itineraries.map((itinerary) =>
            ensure(
              itinerary.flightOnlyItinerary.find(
                (flightIngredient) =>
                  flightIngredient.ingredientId === flightIngredientId
              )
            )
          );
    } catch {
      flightIngredientResponses = [];
    }

    const responseCarrierCodes = flightIngredientResponses.reduce(
      (carrierCodes: string[], response) => {
        if (isAvailableTransportIngredientResponse(response)) {
          carrierCodes.push(...response.allCarriers);
        }

        return carrierCodes;
      },
      []
    );

    return {
      allCarrierCodes: uniq([
        ...flightIngredientCarrierFilters,
        ...responseCarrierCodes,
      ]),
      areAllCarrierCodesAvailable: true,
    };
  }

  function getMainPolygonFullName(
    parentIngredientId: IngredientId,
    staticInfo: DetailsStaticInfo
  ): string | undefined {
    const ingredient =
      selectedItineraryWithStaticInfo.value?.hotelOnlyItinerary.find(
        (hotel) => hotel.ingredientId === parentIngredientId
      );
    const foundPolygon = staticInfo.polygons.find(
      (polygon) => polygon.polygonId === ingredient?.staticInfo?.mainPolygon?.id
    );

    if (!foundPolygon) {
      return;
    }

    return ingredient?.staticInfo?.mainPolygon?.fullName;
  }

  function getMainPolygonId(
    parentIngredientId: IngredientId
  ): number | undefined {
    const ingredient =
      selectedItineraryWithStaticInfo.value?.hotelOnlyItinerary.find(
        (hotel) => hotel.ingredientId === parentIngredientId
      );
    return ingredient?.staticInfo?.mainPolygon?.id;
  }

  // TODO: move to itineraryStore when staticInfoStore is ready
  function getIngredientResponseWithStaticInfo(
    ingredientResponse: ItineraryMainIngredientResponse
  ):
    | OwnStayIngredientResponseWithStaticInfo
    | OwnTransportIngredientResponseWithStaticInfo
    | HotelIngredientResponseWithStaticInfo
    | FlightIngredientResponseWithStaticInfo
    | FerryIngredientResponseWithStaticInfo
    | undefined {
    if (isTransportIngredientResponse(ingredientResponse)) {
      return getTransportIngredientResponseWithStaticInfo(ingredientResponse);
    }

    if (isHotelIngredientResponse(ingredientResponse)) {
      return getHotelIngredientResponseWithStaticInfo(ingredientResponse);
    }

    if (isOwnStayIngredientResponse(ingredientResponse)) {
      return getOwnStayIngredientResponseWithStaticInfo(ingredientResponse);
    }

    if (isOwnTransportIngredientResponse(ingredientResponse)) {
      return getOwnTransportIngredientResponseWithStaticInfo(
        ingredientResponse
      );
    }

    exhaustiveGuard(
      ingredientResponse,
      'Unhandles ingredient response static info'
    );
  }

  function getOwnStayIngredientResponseWithStaticInfo(
    ingredientResponse: OwnStayIngredientResponse
  ): OwnStayIngredientResponseWithStaticInfo | undefined {
    const subIngredientResponseWithStaticInfo =
      getActivityIngredientResponseWithStaticInfo(ingredientResponse);

    if (!subIngredientResponseWithStaticInfo) {
      return;
    }

    let ingredient: RecipeIngredient;

    try {
      ingredient = recipeStore.getIngredient(ingredientResponse.ingredientId);
    } catch {
      return;
    }

    const transportToBeReached = ingredient.transportTypeToBeReached;

    return {
      ...ingredientResponse,
      transportToBeReached,
      staticInfo: null,
      subIngredientResponseWithStaticInfo,
    };
  }

  function getOwnTransportIngredientResponseWithStaticInfo(
    ingredientResponse: OwnTransportIngredientResponse
  ): OwnTransportIngredientResponseWithStaticInfo | undefined {
    return {
      ...ingredientResponse,
      staticInfo: null,
    };
  }

  function getTransportIngredientResponseWithStaticInfo(
    ingredientResponse: FlightIngredientResponse | FerryIngredientResponse
  ): TransportIngredientResponseWithStaticInfo | undefined {
    if (
      !(
        isAvailableTransportIngredientResponse(ingredientResponse) ||
        ingredientResponse.alternativeSuggestion
      )
    ) {
      return {
        ...ingredientResponse,
        staticInfo: undefined,
      };
    }

    const staticInfo = getTransportStaticInfo(ingredientResponse);

    if (!staticInfo) {
      return;
    }

    return {
      ...ingredientResponse,
      staticInfo,
    };
  }

  function getHotelIngredientResponseWithStaticInfo(
    ingredientResponse: HotelIngredientResponse
  ): HotelIngredientResponseWithStaticInfo | undefined {
    const subIngredientResponseWithStaticInfo =
      getActivityIngredientResponseWithStaticInfo(ingredientResponse);

    if (!subIngredientResponseWithStaticInfo) {
      return;
    }

    /**
     * Try/catch prevents error on localhost (rarely on production) when removing a destination,
     * because selectedItineraryWithStaticInfo (computed) is accessed twice
     * and the second time the ingredient to be removed does no longer exists so it cannot be found.
     * But does not work when removing the last destination (still better than before)
     *
     * There is a fix on this branch: https://bitbucket.org/sembo-it/trip-planner-front/branch/bugfix/remove-destination
     * but produces another bug described in trello card: https://trello.com/c/Jjc3tNJ0
     */
    let ingredient: RecipeIngredient;

    try {
      ingredient = recipeStore.getIngredient(ingredientResponse.ingredientId);
    } catch {
      return;
    }

    const transportToBeReached = ingredient.transportTypeToBeReached;

    if (!isAvailableHotelIngredientResponse(ingredientResponse)) {
      if (ingredientResponse.alternativeSuggestion?.code) {
        const staticInfo = getOverviewStaticInfo(
          ingredientResponse.alternativeSuggestion.code,
          ingredientResponse.ingredientId
        );
        return {
          ...ingredientResponse,
          transportToBeReached,
          staticInfo,
          subIngredientResponseWithStaticInfo,
        };
      } else {
        return {
          ...ingredientResponse,
          transportToBeReached,
          staticInfo: undefined,
          subIngredientResponseWithStaticInfo,
        };
      }
    }

    const staticInfo = getOverviewStaticInfo(
      ingredientResponse.suggested.code,
      ingredientResponse.ingredientId
    );

    if (!staticInfo) {
      return;
    }

    const suggestionsStaticInfo = ingredientResponse.suggestions.reduce(
      (
        acc: OverviewDestinationStaticInfo[],
        suggestion: HotelAlternativeOptionWithBasicRooms
      ) => {
        const staticInfo = getOverviewStaticInfo(
          suggestion.code,
          ingredientResponse.ingredientId
        );

        if (staticInfo) {
          acc.push(staticInfo);
        }
        return acc;
      },
      []
    );

    return {
      ...ingredientResponse,
      transportToBeReached,
      staticInfo,
      subIngredientResponseWithStaticInfo,
      suggestionsStaticInfo,
    };
  }

  function getOverviewStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): OverviewDestinationStaticInfo | undefined {
    return destinationOverviewStaticInfo.value[
      getHotelKey(hotelCode, ingredientId)
    ];
  }

  function getActivityIngredientResponseWithStaticInfo(
    ingredientResponse: HotelIngredientResponse | OwnStayIngredientResponse
  ): ActivityIngredientResponseWithStaticInfo[] | undefined {
    const subIngredientResponseWithStaticInfo: (
      | ActivityIngredientResponseWithStaticInfo
      | undefined
    )[] = ingredientResponse.subIngredientResponse.map((response) => {
      if (!isAvailableActivityIngredientResponse(response)) {
        if (response.alternativeSuggestion) {
          const staticInfo = getSubIngredientStaticInfo(
            response.alternativeSuggestion.code,
            response.ingredientId
          );

          if (!staticInfo) {
            return undefined;
          }

          return {
            ...response,
            staticInfo,
          };
        } else {
          return { ...response, staticInfo: undefined };
        }
      }

      const staticInfo = getSubIngredientStaticInfo(
        response.suggested.code,
        response.ingredientId
      );

      if (!staticInfo) {
        return undefined;
      }

      return {
        ...response,
        staticInfo,
      };
    });

    const availableSubIngredientResponseWithStaticInfo =
      subIngredientResponseWithStaticInfo.filter(isDefined);

    if (
      availableSubIngredientResponseWithStaticInfo.length !==
      subIngredientResponseWithStaticInfo.length
    ) {
      return;
    }

    return availableSubIngredientResponseWithStaticInfo;
  }

  function getSubIngredientStaticInfo(
    code: string,
    ingredientId: IngredientId
  ): OverviewActivityStaticInfo | undefined {
    return subIngredientsOverviewStaticInfo.value[
      getHotelKey(code, ingredientId)
    ];
  }

  function getCommonMinimumPolygon(
    heartedHotelCodes: string[],
    ingredientId: IngredientId
  ): Polygon | undefined {
    const staticInfoFromProvidedCodes = heartedHotelCodes
      .map((productCode) =>
        getHeartedHotelStaticInfo(productCode, ingredientId)
      )
      .filter(isDefined);

    if (!staticInfoFromProvidedCodes.length) {
      return;
    }

    const polygonIdsFromProducts = staticInfoFromProvidedCodes.map((product) =>
      product.polygons.map((polygon) => polygon.polygonId)
    );

    const commonPolygonIds = intersection(...polygonIdsFromProducts);

    const firstProduct = staticInfoFromProvidedCodes[0];

    assertValue(
      firstProduct,
      'tripState.ts > getCommonMinimumPolygon > firstProduct'
    );

    const commonPolygons = firstProduct.polygons.filter((polygon) =>
      commonPolygonIds.includes(polygon.polygonId)
    );

    const sorted = sortBy(commonPolygons, (polygon) =>
      parseInt(PolygonType[polygon.type], 10)
    );

    return sorted[0];
  }

  function getHeartedHotelStaticInfo(
    hotelCode: string,
    parentIngredientId: IngredientId
  ): OverviewHeartedStaticInfo | DestinationStaticInfo | undefined {
    const hotelKey = getHotelKey(hotelCode, parentIngredientId);
    const staticInfoDictionary = editingIngredientStore.editingStayIngredient
      ? destinationStaticInfo.value
      : heartedHotelsOverviewStaticInfo.value;
    return staticInfoDictionary[hotelKey];
  }

  function getDurationRangeInMinutesFromItineraries(
    flightIngredientId: IngredientId
  ): DecimalRangeType | undefined {
    const transportIngredientFromSelectedItinerary =
      itineraryStore.selectedItinerary?.flightOnlyItinerary.find(
        (itinerary) => itinerary.ingredientId === flightIngredientId
      );
    if (
      !transportIngredientFromSelectedItinerary?.isAvailable ||
      itineraryStore.isSearchingItineraries
    ) {
      return undefined;
    }
    try {
      const flightIngredientResponses = preferredDay.value
        ? [ensure(transportIngredientFromSelectedItinerary)]
        : itineraryStore.itineraries.map((itinerary) =>
            ensure(
              itinerary.flightOnlyItinerary.find(
                (flightIngredient) =>
                  flightIngredient.ingredientId === flightIngredientId
              )
            )
          );
      const fromDurationRange: number[] = [];
      const toDurationRange: number[] = [];

      flightIngredientResponses.forEach((response): void => {
        if (isAvailableTransportIngredientResponse(response)) {
          if (!response.durationRangeInMinutes) {
            return;
          }
          fromDurationRange.push(response.durationRangeInMinutes.from);
          toDurationRange.push(response.durationRangeInMinutes.to);
        }
      });
      if (fromDurationRange.length && toDurationRange.length) {
        const result = {
          from: Math.min(...fromDurationRange),
          to: Math.max(...toDurationRange),
        };
        return result;
      }
    } catch {
      return undefined;
    }
  }

  function getStaticInfo(
    hotelCode: string,
    ingredientId: IngredientId
  ): DestinationStaticInfo | OverviewDestinationStaticInfo | undefined {
    if (editingIngredientStore.editingStayIngredient) {
      const staticInfoFromDestination = getDestinationStaticInfo(
        hotelCode,
        ingredientId
      );

      if (staticInfoFromDestination) {
        return staticInfoFromDestination;
      }
    }
    return getOverviewStaticInfo(hotelCode, ingredientId);
  }

  function getDestinationStaticInfo(
    code: string,
    ingredientId: IngredientId
  ): DestinationStaticInfo | undefined {
    return destinationStaticInfo.value[getHotelKey(code, ingredientId)];
  }

  function getDayPeriodForStop(
    stop: ChronologicalItinerary,
    tripStartDate?: Date,
    isLastStop?: boolean
  ): string | undefined {
    const period = getDayDurationFromStop(stop, tripStartDate);
    if (!period) {
      return;
    }
    const isSameDay = period[0] === period[1];

    return isLastStop || isSameDay
      ? `${period[1]}`
      : `${period[0]}-${period[1]}`;
  }

  function getDayDurationFromStop(
    stop: ChronologicalItinerary | undefined,
    tripStartDate?: Date
  ): number[] | undefined {
    if (!tripStartDate || !stop) {
      return;
    }
    const firstPeriodValue = 1;
    const tripDay1 = tripStartDate;
    const periodDay1 = new SemboDate(stop.startDate);
    const differenceInDays = periodDay1.diff(tripDay1, 'calendarDay');
    const firstPeriodDay = differenceInDays + firstPeriodValue;
    let lastPeriodDay = firstPeriodDay + stop.numberOfNights;

    stop.ingredients.forEach((ingredient, index, originalArray) => {
      if (
        isAvailableTransportIngredientResponseWithStaticInfo(ingredient) &&
        index === originalArray.length - 1
      ) {
        const transportDuration = getTransportDuration(ingredient);
        lastPeriodDay += transportDuration;
      }
    });

    return [firstPeriodDay, lastPeriodDay];
  }

  function getDestinationInformation(
    ingredientId: IngredientId
  ): DestinationInformation | undefined {
    return destinationsInformation.value.get(ingredientId);
  }

  function getTransportStaticInfo(
    ingredientResponse: FerryIngredientResponse | FlightIngredientResponse
  ): TransportStaticInfo | undefined {
    const portStaticInfo = isFlightIngredientResponse(ingredientResponse)
      ? airportStaticInfo.value
      : seaportStaticInfo.value;

    if (
      isFlightIngredientResponse(ingredientResponse) &&
      (Object.keys(carrierStaticInfo.value).length === 0 ||
        Object.keys(portStaticInfo).length === 0)
    ) {
      return;
    }

    const ingredientPorts = uniq(
      getPortsFromTransportResponse(ingredientResponse)
    );
    const ingredientPortsStaticInfo = getPortsStaticInfo(
      ingredientPorts,
      ingredientResponse.ingredientType
    );

    if (
      ingredientPorts.length !== Object.keys(ingredientPortsStaticInfo).length
    ) {
      return;
    }

    const ingredientCarriers = uniq(
      getCarriersFromTransportResponse(ingredientResponse)
    );
    const ingredientCarriersStaticInfo =
      getCarriersStaticInfo(ingredientCarriers);

    if (
      ingredientCarriers.length !==
      Object.keys(ingredientCarriersStaticInfo).length
    ) {
      return;
    }

    return {
      ingredientId: ingredientResponse.ingredientId,
      carriers: ingredientCarriersStaticInfo,
      ports: ingredientPortsStaticInfo,
    };
  }

  function getCarriersFromTransportResponse(
    ingredientResponse: FlightIngredientResponse | FerryIngredientResponse
  ): string[] {
    if (isAvailableTransportIngredientResponse(ingredientResponse)) {
      return getCarriersFromLegs(ingredientResponse.legs);
    } else {
      if (ingredientResponse.alternativeSuggestion) {
        return getCarriersFromLegs(
          ingredientResponse.alternativeSuggestion.legs
        );
      }
    }
    return [];
  }

  function getCarriersFromLegs(legs: TransportLeg[]): string[] {
    return legs.flatMap((leg) =>
      leg.segments.flatMap((segment) => [
        segment.carrier,
        segment.operatingCarrier,
      ])
    );
  }

  function getCarriersStaticInfo(
    carriers: string[]
  ): Record<string, CarrierStaticInfo> {
    const result: Record<string, CarrierStaticInfo> = {};

    carriers.forEach((carrier) => {
      const carrierInfo = carrierStaticInfo.value[carrier];

      if (carrierInfo) {
        result[carrier] = carrierInfo;
      }
    });
    // FYI: This is a fallback for when the carrierStaticInfo is empty / server returns error.
    if (Object.keys(result).length === 0) {
      const fallbackResult: Record<string, CarrierStaticInfo> = {};

      carriers.forEach((carrier) => {
        fallbackResult[carrier] = {
          code: carrier,
          name: carrier,
          logo: '',
        };

        insertCarrierStaticInfo({
          ...carrierStaticInfo.value,
          ...fallbackResult,
        });

        return fallbackResult;
      });
    }

    return result;
  }

  function getPortsStaticInfo(
    ports: string[],
    kind: IngredientType
  ): Record<string, PortStaticInfo> {
    const portsStaticInfo: Record<string, PortStaticInfo> = {};
    if (kind === IngredientType.Flight) {
      ports.forEach((port) => {
        const airportInfo = airportStaticInfo.value[port];
        if (airportInfo) {
          portsStaticInfo[port] = airportInfo;
        }
      });
    }
    if (kind === IngredientType.Ferry) {
      ports.forEach((port) => {
        const seaportInfo = seaportStaticInfo.value[port];
        if (seaportInfo) {
          portsStaticInfo[port] = seaportInfo;
        }
      });
    }
    return portsStaticInfo;
  }

  function getPortsFromTransportResponse(
    ingredientResponse: FlightIngredientResponse | FerryIngredientResponse
  ): string[] {
    const legs = isAvailableTransportIngredientResponse(ingredientResponse)
      ? ingredientResponse.legs
      : ingredientResponse.alternativeSuggestion?.legs;

    return legs ? getPortsFromLegs(legs) : [];
  }

  function getPortsFromLegs(legs: TransportLeg[]): string[] {
    return legs.flatMap((leg) =>
      leg.segments.flatMap((segment) => [
        segment.departureIata,
        segment.arrivalIata,
      ])
    );
  }

  function getDayAndDatePeriods(
    ingredients: ItineraryMainIngredientResponseWithStaticInfo[],
    startDate: Date,
    tripStartDate: Date
  ): {
    dayPeriod: string | undefined;
    datePeriod: string;
    shortDatePeriod: string;
  } {
    const stayIngredientInGroup = ingredients.find(
      isStayIngredientResponseWithStaticInfo
    );
    const numberOfNights = stayIngredientInGroup?.numberOfNights ?? 0;
    const dayPeriod = getDayPeriodForStop(
      {
        startDate,
        numberOfNights,
        ingredients,
      },
      tripStartDate
    );
    const datePeriod = getDatePeriodForStop({
      format: 'long',
      stop: {
        startDate,
        numberOfNights,
        ingredients,
      },
    });
    const shortDatePeriod = getDatePeriodForStop({
      format: 'short',
      stop: {
        startDate,
        numberOfNights,
        ingredients,
      },
      isSmallDevice: getIsSmallDevice(),
    });
    return { dayPeriod, datePeriod, shortDatePeriod };
  }

  function getCarrierCodesWithStaticInfo(): string[] {
    return Object.keys(carrierStaticInfo.value);
  }

  function getPortCodesWithStaticInfo(kind: IngredientType): string[] {
    if (kind === IngredientType.Ferry) {
      return Object.keys(seaportStaticInfo.value);
    }

    if (kind === IngredientType.Flight) {
      return Object.keys(airportStaticInfo.value);
    }

    return [];
  }

  function getPortStaticInfo(
    code: string,
    kind: IngredientType
  ): PortStaticInfo | undefined {
    if (kind === IngredientType.Ferry) {
      return seaportStaticInfo.value[code];
    }

    if (kind === IngredientType.Flight) {
      return airportStaticInfo.value[code];
    }
  }

  function areThereActivitiesInDestination(destinationId: string): boolean {
    const hasDestinationActivities =
      destinationWithAvailableActivities.value[destinationId];

    return !!hasDestinationActivities;
  }

  function isSuggestedHotel(
    ingredientId: IngredientId,
    hotelCode: string
  ): boolean {
    const hotelIngredient =
      editingIngredientStore.editingStayIngredient &&
      isHotelIngredient(editingIngredientStore.editingStayIngredient)
        ? editingIngredientStore.editingStayIngredient
        : recipeStore.getHotelIngredient(ingredientId);

    const thereAreNoHeartedHotels = !hotelIngredient.heartedHotelCodes.length;

    return (
      thereAreNoHeartedHotels && isHotelInItinerary(ingredientId, hotelCode)
    );
  }

  function isHotelInItinerary(
    ingredientId: IngredientId,
    hotelCode: string
  ): boolean {
    const hotelIngredientResponse =
      itineraryStore.getHotelIngredientResponse(ingredientId);

    const isAvailable =
      !!hotelIngredientResponse &&
      isAvailableHotelIngredientResponse(hotelIngredientResponse);

    const hasSuggestedHotelCode =
      isAvailable && hotelIngredientResponse.suggested.code === hotelCode;

    return hasSuggestedHotelCode;
  }

  function isSuggestedTransport(ingredientId: IngredientId): boolean {
    const transportIngredient =
      editingIngredientStore.editingTransportIngredient ??
      recipeStore.getTransportIngredient(ingredientId);
    return !transportIngredient.transportIds?.length;
  }

  function updateActivitySubIngredients(
    activityCode: string,
    selectedTickets: ActivityTicket[],
    parentIngredientId: IngredientId
  ): void {
    const parentIngredient = recipeStore.getStayIngredient(parentIngredientId);

    parentIngredient.subIngredients = parentIngredient.subIngredients.filter(
      (subIngredient) => subIngredient.code !== activityCode
    );

    const selectedTicketsByDateOffset = groupBy(
      selectedTickets,
      (selectedTicket) => selectedTicket.dateOffset.toString()
    );

    for (const dateOffset in selectedTicketsByDateOffset) {
      const tickets = selectedTicketsByDateOffset[dateOffset];
      assertValue(
        tickets,
        'tripState.ts > updateActivitySubIngredients > ticket'
      );
      const getQuantityFromPaxValue = selectedTickets.find(
        (ticket) => ticket.code === activityCode
      )?.getQuantityFromPax;

      const newSubIngredient = new ActivityIngredient(
        activityCode,
        parentIngredientId
      );

      newSubIngredient.offset = Number(dateOffset);
      newSubIngredient.tickets = tickets.map(
        ({ rateId, ageRangeId, quantity, timeSlotId, supplierToken }) => ({
          rateId,
          ageRangeId,
          quantity,
          timeSlotId,
          supplierToken,
          getQuantityFromPax: getQuantityFromPaxValue,
        })
      );
      parentIngredient.subIngredients.push(newSubIngredient);
    }
  }

  function updateIngredientCommonPolygonText(
    hotelIngredient: HotelIngredient
  ): void {
    const { heartedHotelCodes } = hotelIngredient;

    const commonMinimumPolygon = getCommonMinimumPolygon(
      heartedHotelCodes,
      hotelIngredient.ingredientId
    );

    hotelIngredient.commonPolygon = {
      name: commonMinimumPolygon?.fullName,
      id: commonMinimumPolygon?.polygonId,
    };
  }

  function updateIngredientText(hotelIngredient: HotelIngredient): void {
    const newSearchText =
      hotelIngredient.nearbyPolygon?.name ??
      hotelIngredient.commonPolygon?.name;

    const newPolygonId =
      hotelIngredient.nearbyPolygon?.id ?? hotelIngredient.commonPolygon?.id;

    if (newSearchText) {
      hotelIngredient.setSearchTerm({
        type: SearchTermType.FreeText,
        freeText: newSearchText,
      });
    } else if (newPolygonId) {
      hotelIngredient.setSearchTerm({
        type: SearchTermType.PolygonId,
        polygonId: newPolygonId,
      });
    }
  }

  function updateSessionId(value: string): void {
    sessionId.value = value;
  }

  function updateDestinationStaticInfo(): void {
    destinationStaticInfo.value = Object.assign(newDestinationStaticInfo.value);
  }

  function updateAreAllSelectedRatesValid(value: boolean): void {
    areAllSelectedRatesValid.value = value;
  }

  function updateEndLocationIsManuallyTyped(value: boolean): void {
    endLocationIsManuallyTyped.value = value;
  }

  function updateStaysShown(value: number): void {
    staysShown.value = value;
  }

  function updateSearchingFerriesStaticInfo(value: boolean): void {
    isSearchingFerriesStaticInfo.value = value;
  }

  function updateSearchingFlightsStaticInfo(value: boolean): void {
    isSearchingFlightsStaticInfo.value = value;
  }

  function updateDestinationAvailability(): void {
    destinationAvailability.value = Object.assign(
      newDestinationAvailability.value
    );
  }

  function updateFerriesVehicleTypes(
    newFerriesVehicleTypes: TransportVehicle[]
  ): void {
    ferryVehicleTypes.value = newFerriesVehicleTypes;
  }

  function updateDestinationWithAvailableActivities(
    destinationId: string,
    availableActivities: boolean
  ): void {
    destinationWithAvailableActivities.value[destinationId] =
      availableActivities;
  }

  function toggleHeartHotel(
    heartedHotel: HeartedHotel,
    parentIngredientId: IngredientId
  ): void {
    const provisionalHeartedHotel: HeartedHotel = { ...heartedHotel };

    const parentIngredient = recipeStore.getHotelIngredient(parentIngredientId);

    const newHeartedHotels = [...parentIngredient.heartedHotels];

    const heartedHotelPredicate = (existing: HeartedHotel): boolean =>
      provisionalHeartedHotel.code === existing.code;

    const roomCodes =
      heartedHotel.rooms?.map((room) => room.code) ?? heartedHotel.roomCodes;

    const shouldDeleteHearted =
      parentIngredient.heartedHotels.includes(provisionalHeartedHotel) ||
      (parentIngredient.heartedHotels.length && !roomCodes?.length);

    const toggleMode = shouldDeleteHearted
      ? ToggleMode.Delete
      : ToggleMode.Replace;

    replaceOrDeleteValues(
      provisionalHeartedHotel,
      newHeartedHotels,
      heartedHotelPredicate,
      toggleMode
    );

    parentIngredient.heartedHotels = newHeartedHotels;
    updateIngredientCommonPolygonText(parentIngredient);
    updateIngredientText(parentIngredient);
  }

  async function toggleExpandedMonth(month: Date): Promise<void> {
    expandMonth(month);

    if (expandedMonth.value) {
      const isCurrentMonth = isDateInExpandedMonth(new Date());

      const monthHasAlreadyOpened =
        startSearchDate.value && isDateInExpandedMonth(startSearchDate.value);

      if (!monthHasAlreadyOpened || startSearchDate.value === undefined) {
        if (!isCurrentMonth) {
          trackMonthChanged();
        }
        clearPreferredDay();
        startSearchDate.value = isCurrentMonth
          ? new Date()
          : new SemboDate(expandedMonth.value).startOf('month').get();

        endSearchDate.value = new SemboDate(expandedMonth.value)
          .endOf('month')
          .get();
      }

      await nextTick();

      const timePerDate = 20;
      const selectedElement = document.querySelector<HTMLElement>(
        '.alternative-itinerary-month.selected'
      );
      const datesInMonth = itineraryStore.itineraryDates.length;
      const timePerMonth = `${timePerDate * datesInMonth}ms`;
      if (selectedElement) {
        selectedElement.style.animationDuration = timePerMonth;
      }
    }
  }

  function hotelIsHearted(
    hotelCode: string,
    parentIngredientId: IngredientId
  ): boolean {
    const parentIngredient = recipeStore.getStayIngredient(parentIngredientId);

    return (
      isHotelIngredient(parentIngredient) &&
      parentIngredient.heartedHotelCodes.includes(hotelCode)
    );
  }

  function addDestinationInformation(
    newDestinationInformation: DestinationInformation,
    ingredientId: IngredientId
  ): void {
    destinationsInformation.value.set(ingredientId, {
      destination: newDestinationInformation.destination,
      description: newDestinationInformation.description,
      image: newDestinationInformation.image,
    });
  }

  function clearDestinationStaticInfo(): void {
    destinationStaticInfo.value = {};
  }

  function clearNewDestinationStaticInfo(): void {
    newDestinationStaticInfo.value = {};
  }

  function clearDestinationAvailability(): void {
    destinationAvailability.value = {};
  }

  function clearNewDestinationAvailability(): void {
    newDestinationAvailability.value = {};
  }

  function clearOverviewStaticInfo(): void {
    destinationOverviewStaticInfo.value = {};
    subIngredientsOverviewStaticInfo.value = {};
    heartedHotelsOverviewStaticInfo.value = {};
  }

  function clearTransportList(): void {
    setTransportList([]);
  }

  function setCarrierStaticInfo(
    newCarrierInfo: Record<string, CarrierStaticInfo>
  ): void {
    carrierStaticInfo.value = newCarrierInfo;
  }

  function setCarrierNames(newCarrierNames: Record<string, CarrierName>): void {
    carrierNames.value = newCarrierNames;
  }

  function setFlightStaticInfoToRecipe(): void {
    recipeStore.flightOnlyRecipe.forEach((flightIngredient) => {
      assertValue(
        flightIngredient.fromPort,
        'tripState > setFlightStaticInfoToRecipe > fromPort'
      );
      assertValue(
        flightIngredient.toPort,
        'tripState > setFlightStaticInfoToRecipe > toPort'
      );

      const fromPortStaticInfo = getPortStaticInfo(
        flightIngredient.fromPort,
        IngredientType.Flight
      );
      const toPortStaticInfo = getPortStaticInfo(
        flightIngredient.toPort,
        IngredientType.Flight
      );

      flightIngredient.fromPortText = fromPortStaticInfo?.position.name;
      flightIngredient.fromPortDisplayText = fromPortStaticInfo?.name;
      flightIngredient.toPortText = toPortStaticInfo?.position.name;
      flightIngredient.toPortDisplayText = toPortStaticInfo?.name;
    });
  }

  function setTransportList(
    newTransportList: AvailableTransportIngredientResponseWithStaticInfo[],
    selected?: AvailableTransportIngredientResponseWithStaticInfo
  ): void {
    if (selected) {
      transportList.value = [
        selected,
        ...newTransportList.filter(
          (transport) =>
            transport.addToBasketToken !== selected?.addToBasketToken
        ),
      ];
    } else {
      transportList.value = newTransportList;
    }
  }

  function setFerryStaticInfoToRecipe(): void {
    recipeStore.ferryOnlyRecipe.forEach((ferryIngredient) => {
      const ferryFromPort = ferryIngredient.fromPort;
      const ferryToPort = ferryIngredient.toPort;
      assertValue(
        ferryFromPort,
        'tripState > setFerryStaticInfoToRecipe > ferryFromPort'
      );
      assertValue(
        ferryToPort,
        'tripState > setFerryStaticInfoToRecipe > ferryToPort'
      );

      const fromPort = Object.keys(seaportStaticInfo.value).find((key) =>
        key.includes(ferryFromPort)
      );
      const toPort = Object.keys(seaportStaticInfo.value).find((key) =>
        key.includes(ferryToPort)
      );

      assertValue(
        fromPort,
        'tripState > setFerryStaticInfoToRecipe > fromPort'
      );
      assertValue(toPort, 'tripState > setFerryStaticInfoToRecipe > toPort');
      const fromPortStaticInfo = getPortStaticInfo(
        fromPort,
        IngredientType.Ferry
      );
      const toPortStaticInfo = getPortStaticInfo(toPort, IngredientType.Ferry);

      ferryIngredient.fromPortText = fromPortStaticInfo?.position.name;
      ferryIngredient.fromPortDisplayText = fromPortStaticInfo?.name;
      ferryIngredient.toPortText = toPortStaticInfo?.position.name;
      ferryIngredient.toPortDisplayText = toPortStaticInfo?.name;
    });
  }

  function replaceHeartedHotel(
    heartedHotel: HeartedHotel,
    ingredientId: IngredientId
  ): void {
    const ingredient = recipeStore.getHotelIngredient(ingredientId);
    ingredient.heartedHotels = [heartedHotel];
    updateIngredientCommonPolygonText(ingredient);
    updateIngredientText(ingredient);
  }

  function insertCarrierStaticInfo(
    newCarrierInfo: Record<string, CarrierStaticInfo>
  ): void {
    Object.assign(carrierStaticInfo.value, newCarrierInfo);
  }

  function insertOverviewStaticInfo({
    destinationStaticInfo,
    subIngredientsStaticInfo,
    heartedHotelsStaticInfo,
  }: OverviewStaticInfoResult): void {
    for (const destinationKey in destinationStaticInfo) {
      const destinationValue = destinationStaticInfo[destinationKey];
      assertValue(
        destinationValue,
        'tripState.ts > insertOverviewStaticInfo > destinationValue'
      );

      destinationOverviewStaticInfo.value[
        getHotelKey(destinationValue.hotelCode, destinationValue.ingredientId)
      ] ??= destinationValue;
    }
    for (const subIngredientKey in subIngredientsStaticInfo) {
      const subIngredientValue = subIngredientsStaticInfo[subIngredientKey];
      assertValue(
        subIngredientValue,
        'tripState.ts > insertOverviewStaticInfo > subIngredientValue'
      );

      subIngredientsOverviewStaticInfo.value[
        getHotelKey(
          subIngredientValue.hotelCode,
          subIngredientValue.ingredientId
        )
      ] ??= subIngredientValue;
    }
    for (const heartedHotelKey in heartedHotelsStaticInfo) {
      const heartedHotelValue = heartedHotelsStaticInfo[heartedHotelKey];
      assertValue(
        heartedHotelValue,
        'tripState.ts > insertOverviewStaticInfo > heartedHotelValue'
      );

      heartedHotelsOverviewStaticInfo.value[
        getHotelKey(heartedHotelValue.hotelCode, heartedHotelValue.ingredientId)
      ] ??= heartedHotelValue;
    }
  }

  function insertNewDestinationAvailability(
    availability: ProductCheapestAvailabilityResponse[]
  ): void {
    availability.forEach((productAvailability) => {
      newDestinationAvailability.value[productAvailability.placeCode] =
        productAvailability;
      destinationAvailability.value[productAvailability.placeCode] =
        productAvailability;
    });
  }

  function increaseDestinationAvailabilitySearchCounter(): void {
    destinationAvailabilitySearchCounter.value++;
  }

  function insertNewDestinationStaticInfo(
    newStaticInfo: Record<string, DestinationStaticInfo>
  ): void {
    Object.assign(newDestinationStaticInfo.value, newStaticInfo);
    Object.assign(destinationStaticInfo.value, newStaticInfo);
  }

  function sortEntriesByDate(
    a: [string, ItineraryMainIngredientResponseWithStaticInfo[]],
    b: [string, ItineraryMainIngredientResponseWithStaticInfo[]]
  ): number {
    const baseA = a[0].replace(/-\d+$/, '');
    const baseB = b[0].replace(/-\d+$/, '');
    if (baseA !== baseB) {
      return baseA.localeCompare(baseB);
    }
    return a[0].length === b[0].length
      ? a[0].localeCompare(b[0])
      : a[0].length > b[0].length
      ? -1
      : 1;
  }

  function sortIngredientsByTransport(
    ingredients: ItineraryMainIngredientResponseWithStaticInfo[]
  ): ItineraryMainIngredientResponseWithStaticInfo[] {
    return [...ingredients].sort((a, b) => {
      const isTransportA =
        isAvailableTransportIngredientResponseWithStaticInfo(a);
      const isTransportB =
        isAvailableTransportIngredientResponseWithStaticInfo(b);

      if (isTransportA && isTransportB) {
        return new SemboDate(a.departureDate).diff(
          new SemboDate(b.departureDate).get(),
          'second'
        );
      }

      return 0;
    });
  }

  function insertCarrierNames(
    newCarrierNames: Record<string, CarrierName>
  ): void {
    Object.assign(carrierNames.value, newCarrierNames);
  }

  function insertPortStaticInfo(
    newPortStaticInfo: Record<string, PortStaticInfo>,
    kind: IngredientType
  ): void {
    if (kind === IngredientType.Ferry) {
      Object.assign(seaportStaticInfo.value, newPortStaticInfo);
    }

    if (kind === IngredientType.Flight) {
      Object.assign(airportStaticInfo.value, newPortStaticInfo);
    }
  }

  function decreaseDestinationAvailabilitySearchCounter(): void {
    destinationAvailabilitySearchCounter.value--;
  }

  function increaseStaticDestinationAvailabilitySearchCounter(): void {
    staticDestinationAvailabilitySearchCounter.value++;
  }

  function stayIsLocked(
    stay: HotelBookableOptionWithRooms,
    ingredientId?: IngredientId
  ): boolean | undefined {
    if (!ingredientId) {
      return false;
    }
    const ingredient = recipeStore.getHotelIngredient(ingredientId);

    const heartedHotel = ingredient.heartedHotels.find(
      (hotel) => hotel.code === stay.code
    );

    if (!heartedHotel) {
      return false;
    }

    const heartedRooms: { code: string; boardBasis?: string }[] | undefined =
      heartedHotel.rooms ?? heartedHotel.roomCodes?.map((code) => ({ code }));

    return (
      heartedHotel.isRefundable === stay.isRefundable &&
      heartedRooms?.every((heartedRoom) =>
        stay.rooms.some(
          (room) =>
            room.roomCode === heartedRoom.code &&
            (!heartedRoom.boardBasis ||
              heartedRoom.boardBasis === room.boardBasis)
        )
      )
    );
  }

  function increaseTransportListSearchCounter(): void {
    transportListSearchCounter.value++;
  }

  function decreaseStaticDestinationAvailabilitySearchCounter(): void {
    staticDestinationAvailabilitySearchCounter.value--;
  }

  function decreaseTransportListSearchCounter(): void {
    transportListSearchCounter.value--;
  }

  return {
    // State
    chronologicalItinerary: readonlyRef(chronologicalItinerary),
    destinationProducts: readonlyRef(destinationProducts),
    isSearchingDestinationAvailability: readonlyRef(
      isSearchingDestinationAvailability
    ),
    selectedItineraryWithStaticInfo: readonlyRef(
      selectedItineraryWithStaticInfo
    ),
    months: readonlyRef(months),
    areAllSelectedRatesValid: readonlyRef(areAllSelectedRatesValid),
    ferryVehicleTypes: readonlyRef(ferryVehicleTypes),
    sessionId: readonlyRef(sessionId),
    isLocationManuallyChanged: readonlyRef(isLocationManuallyChanged),
    preselectedHotel: readonlyRef(preselectedHotel),
    startingLocationIsManuallyTyped: readonlyRef(
      startingLocationIsManuallyTyped
    ),
    // Actions
    getDatePeriodForStop,
    getFlightAllCarrierCodes,
    getPortCodesWithStaticInfo,
    getPortStaticInfo,
    getMainPolygonFullName,
    getMainPolygonId,
    getDestinationInformation,
    getCarrierCodesWithStaticInfo,
    getDurationRangeInMinutesFromItineraries,
    getStaticInfo,
    getDayPeriodForStop,
    getFlightAllCarriers,
    getFerryAllCarriers,
    getTransportStaticInfo,
    getOverviewStaticInfo,
    updateStaysShown,
    updateDestinationWithAvailableActivities,
    updateFerriesVehicleTypes,
    updateSearchingFerriesStaticInfo,
    updateEndLocationIsManuallyTyped,
    updateSessionId,
    updateActivitySubIngredients,
    updateDestinationStaticInfo,
    updateAreAllSelectedRatesValid,
    updateDestinationAvailability,
    updateSearchingFlightsStaticInfo,
    clearTransportList,
    clearOverviewStaticInfo,
    clearNewDestinationAvailability,
    clearDestinationStaticInfo,
    clearNewDestinationStaticInfo,
    clearDestinationAvailability,
    setTransportList,
    setCarrierStaticInfo,
    setFlightStaticInfoToRecipe,
    setFerryStaticInfoToRecipe,
    setCarrierNames,
    insertCarrierNames,
    insertNewDestinationStaticInfo,
    insertCarrierStaticInfo,
    insertOverviewStaticInfo,
    insertNewDestinationAvailability,
    insertPortStaticInfo,
    isSuggestedHotel,
    isSuggestedTransport,
    isHotelInItinerary,
    increaseTransportListSearchCounter,
    increaseDestinationAvailabilitySearchCounter,
    increaseStaticDestinationAvailabilitySearchCounter,
    decreaseTransportListSearchCounter,
    decreaseDestinationAvailabilitySearchCounter,
    decreaseStaticDestinationAvailabilitySearchCounter,
    toggleExpandedMonth,
    toggleHeartHotel,
    stayIsLocked,
    destinationStaticInfo,
    areThereActivitiesInDestination,
    hotelIsHearted,
    addDestinationInformation,
    replaceHeartedHotel,
  };
});
